import React, { useState, useEffect } from "react";
import Table from "../components/Table/Table";
import TopNavbar from "../components/TopNavbar/TopNavbar";
import ApiManager from "../ApiManager/ApiManager";
import NewPettyCash from "./NewPettyCash";
import FilterBar from "../components/FilterBar/FilterBar";
import { useAuth } from "../components/AuthProvider";
import { ToastContainer, toast } from "react-toastify";
import {
  ShowButton,
  ShowComponent,
  hasPermission,
} from "../components/RoleBasedComponents";
import PERMISSIONS from "../permissions";
import Modal from "../components/Modal/Modal";
import { useComponentLoading } from "../utils/useComponentLoading";
import { LoadingSpinner } from "../components/LoadingSpinner/LoadingSpinner";
import EditEntity from "../components/EditEntity/EditEntity";
import { prepareFileForModal } from "../utils/fileUtils";
import FileViewer from "../components/FileViewer/FileViewer";
function PettyCash() {
  const { userPermissions } = useAuth();
  const { withLoading, isLoading, getMessage, getProgress } =
    useComponentLoading();

  const [data, setData] = useState([]);
  const [pettyCashData, setPettyCashData] = useState([]);
  const [rowId, setRowId] = useState(null);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [isModalOpen, setModalOpen] = useState(false);
  const [showFile, setShowFile] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [isEdit, setEdit] = useState(false);
  const [currentRowId, setCurrentRowId] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [driverMap, setDriverMap] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleModalClose = () => {
    setModalOpen(false);
    setShowFile(false);
  };

  const handleEdit = () => {
    console.log("Edit clicked for row:");
    setEdit(true);
    setModalOpen(true);
    fetchPettyCashData();
    fetchDrivers();
  };

  const handleDelete = async () => {
    console.log("Delete clicked for row:");

    try {
      await withLoading(
        "deletePettyCash",
        async (updateProgress) => {
          updateProgress(10, "Deleting petty cash...");
          const query = new URLSearchParams({ id: currentRowId });
          const response = await ApiManager.removePettyCash(query);
          updateProgress(100, "Petty cash deleted");
          toast.success("Petty cash deleted successfully", {
            style: {
              fontSize: "var(--font-size-sm)",
              fontFamily: 'var(--font-family)'
            }
          });
          fetchData();
        },
        "Deleting petty cash..."
      );
    } catch (error) {
      console.error("Error deleting petty cash:", error);
      toast.error("Error deleting petty cash", {
        style: {
          fontSize: "var(--font-size-sm)",
          fontFamily: 'var(--font-family)'
        }
      });
    }
  };

  const handleFileClick = async (fileName) => {
    try {
      // Open modal first to show loading state
      setModalOpen(true);
      setShowFile(true);

      await withLoading(
        "fetchFile",
        async (updateProgress) => {
          updateProgress(10, "Fetching file...");

          // Use the utility function to prepare the file for viewing in the modal
          await prepareFileForModal(
            fileName,
            setFileUrl,
            setFileType,
            (progress) => {
              // Map the progress from the utility function (0-100) to our progress range (10-90)
              updateProgress(10 + (progress * 0.8), "Processing file...");
            }
          );

          updateProgress(100, "File loaded successfully");
        },
        "Loading file..."
      );
    } catch (error) {
      console.error("Error fetching file:", error);
      setModalOpen(false);
      setShowFile(false);
      toast.error("Failed to load file. Please try again.");
    }
  };

  const buttons = [];

  if (hasPermission(userPermissions, PERMISSIONS.VENDOR_PETTY_CASH_UPDATE)) {
    buttons.push({ label: "Edit", onClick: () => handleEdit() });
    buttons.push({ label: "Delete", onClick: () => {
      setCurrentRowId(rowId);
      setShowDeleteConfirm(true);
    }});
  }

  useEffect(() => {
    fetchData();
  }, [filters, currentPage]);

  const fetchData = async () => {
    try {
      await withLoading(
        "fetchPettyCash",
        async (updateProgress) => {
          updateProgress(10, "Fetching petty cash data...");
          const params = new URLSearchParams({
            page_size: 12,
            page: currentPage,
            driverId: filters.driverId || "",
          });

          updateProgress(30, "Making API request...");
          const response = await ApiManager.getAllPettyCash(params);
          if (!response.ok) throw new Error("failed to fetch petty cash");

          updateProgress(60, "Processing response...");
          const json = await response.json();

          updateProgress(90, "Updating UI...");
          setData(json);
          setHasMorePages(json.length == 12);
          updateProgress(100, "Data loaded successfully");
        },
        "Loading petty cash data..."
      );
    } catch (error) {
      console.log("error", error);
      setHasMorePages(false);
    }
  };

  const fetchPettyCashData = async () => {
    try {
      await withLoading(
        "fetchPettyCashData",
        async (updateProgress) => {
          updateProgress(10, "Fetching petty cash data...");
          setCurrentRowId(rowId);
          const query = new URLSearchParams({ id: rowId });
          const response = await ApiManager.getPettyCash(query);
          if (!response.ok) throw new Error("Network response was not ok");
          const json = await response.json();
          setPettyCashData(json);
          updateProgress(100, "Petty cash data loaded");
        },
        "Loading petty cash data..."
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchDrivers = async () => {
    try {
      await withLoading(
        "fetchDrivers",
        async (updateProgress) => {
          updateProgress(10, "Fetching drivers...");
          const response = await ApiManager.getAllDrivers();
          if (!response.ok) throw new Error("Failed to fetch drivers");
          const json = await response.json();
          setDrivers(json);
          setDriverMap(json.reduce((acc, driver) => {
            acc[driver.firstName + " " + driver.lastName] = driver.id;
            return acc;
          }, {}));
          updateProgress(100, "Drivers loaded");
        },
        "Loading drivers..."
      );
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  const toggleModal = () => {
    setModalOpen(!isModalOpen);
  };

  const filterOptions = [{ value: "driver", label: "Driver" }];

  const apiFunctions = {
    driver: ApiManager.getAllDrivers,
  };

  const handleFilterChange = (filterValues) => {
    setFilters(filterValues);
  };

  const driverOptions = drivers.map(driver => (driver.firstName + " " + driver.lastName));

  const columns = [
    { field: "driver_name", headerName: "Driver" },
    { field: "petty_cash_given", type: "datetime-local", headerName: "Petty Cash Given Date" },
    { field: "created_at", type: "datetime-local", headerName: "Created At" },
    { field: "amount", headerName: "Amount" },
    { field: "description", headerName: "Description" },
  ];

  const editColumns = [
    { name: "driver_name", label: "Driver", type: "picklist", options: driverOptions },
    { name: "petty_cash_given", label: "Petty Cash Given Date", type: "datetime-local" },
    { name: "amount", label: "Amount", type: "number" },
    { name: "description", label: "Description", type: "text" },
  ];

  const rows = (data || []).map((item) => ({
    id: item.id,
    amount: item.amount,
    description: item.description,
    driver_id: item.driver_id,
    driver_name: item.driver_name,
    petty_cash_given: item.petty_cash_given,
    created_at: item.created_at,
  }));

  const handleEditPettyCash = async (formData) => {
    return await withLoading(
      "editPettyCash",
      async (updateProgress) => {
        updateProgress(10, "Updating petty cash...");
        const updatedFormData = {
          ...formData,
          driver_id: driverMap[formData.driver_name],
        };
        const query = new URLSearchParams({ id: currentRowId });
        const response = await ApiManager.updatePettyCash(query, updatedFormData);
        updateProgress(100, "Petty cash updated");
        return response;
      },
      "Updating petty cash..."
    );
  };

  return (
    <div>
      <ToastContainer/>
      <TopNavbar title="Petty Cash" />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          paddingTop: "20px",
          height: "5vh",
        }}
      >
        <FilterBar
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          apiFunctions={apiFunctions}
        />
        <ShowButton permission={PERMISSIONS.VENDOR_PETTY_CASH_CREATE}>
          <NewPettyCash onSuccess={fetchData} />
        </ShowButton>
      </div>

      <Table
        table_columns={columns}
        view_columns={columns}
        rows={rows}
        setRowsId={setRowId}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        hasMorePages={hasMorePages}
        isLoading={isLoading("fetchPettyCash")}
        loadingMessage={getMessage("fetchPettyCash")}
        loadingProgress={getProgress("fetchPettyCash")}
        buttons={buttons}
      />

      {isModalOpen && isEdit && (
        <div className="edit-container">
          <Modal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            title="Edit Petty Cash"
          >{isLoading("fetchPettyCashData") ? (
            <div className="loading-container">
              <LoadingSpinner
                size="large"
                message={getMessage("fetchPettyCashData") || "Loading petty cash data..."}
                progress={getProgress("fetchPettyCashData")}
                showProgress={true}
              />
            </div>
          ) : (
            <EditEntity
              id={rowId}
              updateFunction={handleEditPettyCash}
              prevData={pettyCashData}
              columns={editColumns}
              toggleFunction={toggleModal}
              title="Edit Petty Cash"
              onSuccess={fetchData}
          />
          )}
          </Modal>
        </div>
      )}

      {isModalOpen && showFile && (
        <div className="file-container">
          <Modal isOpen={isModalOpen} onClose={handleModalClose}>
            {isLoading("fetchFile") ? (
              <LoadingSpinner
                size="medium"
                message={getMessage("fetchFile")}
                progress={getProgress("fetchFile")}
              />
            ) : fileUrl ? (
              <div style={{ width: "100%", height: "500px" }}>
                <FileViewer
                  fileUrl={fileUrl}
                  fileType={fileType}
                  fileName={fileUrl ? fileUrl.split('/').pop() : 'petty-cash-receipt'}
                />
              </div>
            ) : (
              <p>Loading file...</p>
            )}
          </Modal>
        </div>
      )}

      {showDeleteConfirm && (
        <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title="Confirm Delete">
          <div style={{ padding: "1rem" }}>
            <p>Are you sure you want to delete this petty cash entry?</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
              <button
                className="custom-button-red"
                onClick={async () => {
                  setShowDeleteConfirm(false);
                  await handleDelete();
                }}
              >
                Yes, Delete
              </button>
              <button
                className="custom-button"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default PettyCash;
